import React, { useContext, useEffect, useRef, useState } from 'react';

import addSquareIcon from '@assets/img/icons/add-square.svg';
import refreshIcon from '@assets/img/icons/refresh.svg';
import subtractSquareIcon from '@assets/img/icons/subtract-square.svg';
import { Button } from '@components/Button';
import { IconButton } from '@components/IconButton';
import { GridForm } from '@components/NextForms/GridForm';
import { ContactDisplay } from '@components/utilities/BookingInquiry/ContactDisplay';
import CheckIcon from '@material-ui/icons/Check';
import { type Contact, ContactSearchQuery } from '@models/next/Contact';
import { empty } from '@monoid-dev/mobx-zod-form';
import { useForm } from '@monoid-dev/mobx-zod-form-react';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import { useEventCreator } from '@utils/useEventCreator';
import clsx from 'clsx';
import Color from 'color';
import useIntersection from 'react-use/lib/useIntersection';

const ContactFilterContext = React.createContext<ContactFilterProps>({
  selectedIds: [],
  onChangeSelectedIds: () => {},
  contacts: [],
  title: '',
  fetchNextPage: () => {},
  isEnglishNameRequired: true,
});

const strings = createStrings({
  ja: {
    companyName: '会社名',
    principalName: '担当者名',
    addContact: (title: string) => `新しい${title}を追加`,
    listContact: (title: string) => `${title}を編集または削除`,
    addOrModifyContact: (title: string) => `${title}の追加もしくは編集`,
    space: '　',
    noContent: (title: string, onClick?: () => void) => (
      <>
        まだ{title}を登録していません。
        <br />
        {onClick && (
          <span>
            「
            <a onClick={onClick}>
              新しい
              {title}
              を追加
            </a>
            」をクリックして{title}を登録してください。
            <br />
          </span>
        )}
      </>
    ),
    noResult: (title: string) => (
      <>
        お探しの
        {title}
        は見つかりません。ほかの条件で検索してください。
      </>
    ),
    missingInfo: (missZipcode: boolean, missEnglishName: boolean, url?: string) => (
      <>
        {[missZipcode && '郵便番号', missEnglishName && '社名の英語表記']
          .filter(Boolean)
          .join('と')}
        が入力されていません。
        <br />
        {url && (
          <span>
            <a href={url} target="_blank">
              こちら
            </a>
            から追記をお願いいたします。
          </span>
        )}
      </>
    ),
    choose: '選択する',
    chosen: '選択済み',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    padding: '1rem 1rem',
  },
  contactFilterContainer: {
    ...common.flexColumn,
    rowGap: '1rem',
    paddingTop: '1rem',
    paddingBottom: '1rem',
  },
  contactListFilterContainer: {
    ...common.flexInline,
    alignItems: 'flex-end',
    columnGap: '1.5rem',
    flexWrap: 'wrap',
    [theme.breakpoints.down('xs')]: {
      rowGap: '1.5rem',
      justifyContent: 'center',
    },
  },
  contactFilterRoot: {
    [theme.breakpoints.down('sm')]: {
      width: '30%',
    },
    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
  },
  label: {
    minWidth: 'min-content',
    width: 'initial',
  },
  actionsContainer: {
    ...common.flexInline,
    flexWrap: 'wrap',
    columnGap: '1.5rem',
    [theme.breakpoints.down('sm')]: {
      columnGap: '8rem',
      rowGap: '0.5rem',
    },
    [theme.breakpoints.down('xs')]: {
      ...common.flexColumn,
    },
  },
  contactItemContainer: {
    ...common.flexInline,
    ...common.itemsCenter,
    ...common.justifyBetween,
    flex: 1,
    padding: '20px 20px',
    backgroundColor: theme.palette.grey[100],
    minWidth: 0,
    [theme.breakpoints.down('xs')]: {
      width: '297px',
      padding: '1rem',
    },
  },
  contactItemWrapper: {
    ...common.flexInline,
    columnGap: 1,
  },
  contactItemIndicator: {
    backgroundColor: theme.palette.grey[100],
    width: 12,
    flex: '0 0 12px',
    borderRadius: 2,
    '$selected &': {
      backgroundColor: theme.palette.primary.main,
    },
    '$disabled &': {
      backgroundColor: theme.palette.grey[600],
    },
  },
  contactListContainer: {
    ...common.flexColumn,
    overflow: 'auto',
    height: 360,
    rowGap: 10,
    [theme.breakpoints.down('xs')]: {
      height: '23vh',
    },
  },
  contactItemRow: {
    ...common.flexInline,
  },
  contactItemColumn: {
    minWidth: 200,
  },
  contactItemSelectedDot: {
    height: 16,
    width: 16,
    flex: '0 0 16px',
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.grey[200]}`,
    borderRadius: '50%',
    '$selected &': {
      backgroundColor: theme.palette.primary.main,
      border: `1px solid ${theme.palette.background.paper}`,
    },
    '$disabled &': {
      backgroundColor: theme.palette.grey[600],
      border: `1px solid ${theme.palette.background.paper}`,
    },
  },
  contactItemSelectButton: {
    ...common.flexCenter,
    ...common.textPrimary,
    ...common.bigText,
    width: 111,
    height: 38,
    border: `1px solid ${theme.palette.primary.main}`,
    backgroundColor: theme.palette.background.paper,
    '$selected &': {
      border: `1px solid ${theme.palette.common.white}`,
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
    },
  },
  contactItemSelectCover: {
    ...common.flexCenter,
    position: 'absolute',
    zIndex: 10,
    width: '100%',
    height: '100%',
    backgroundColor: Color(theme.palette.grey[700]).alpha(0.7).rgb().string(),
  },
  contactItemSelectCoverInner: {
    ...common.flexColumnCenter,
    ...common.textWhite,
    ...common.bigText,
    ...common.fontWeight700,
    alignItems: 'start',
    flex: 1,
    maxWidth: 'max-content',
    lineHeight: '2rem',
    '& a': {
      textDecoration: 'underline',
      textDecorationThickness: '1px',
      textUnderlineOffset: '1px',
      ...common.baseButton,
    },
    [theme.breakpoints.down('sm')]: {
      padding: '0 1rem',
    },
  },
  selected: {},
  disabled: {},
  noContent: {
    ...common.textGray500,
    ...common.smallerText,
    ...common.textAlignCenter,
    paddingTop: 40,
    height: 360,
    '& a': {
      ...common.caution,
      ...common.baseButton,
      textDecoration: 'underline',
    },
  },
}));

interface ContactItemProps {
  selected: boolean;
  disabled: boolean;
  contact: Contact;
  onSelect: () => void;
}

const ContactItem = React.memo(
  React.forwardRef<HTMLDivElement, ContactItemProps>((props, ref) => {
    const { selected, disabled, contact, onSelect } = props;

    const classes = {
      ...useCommonStyles(),
      ...useStyles(),
    };

    const isShipper = contact.contact_type === 'shipper';

    return (
      <div
        ref={ref}
        onClick={onSelect}
        className={clsx(
          classes.contactItemWrapper,
          !disabled && classes.baseButton,
          selected && classes.selected,
          disabled && classes.disabled,
        )}
      >
        <div className={classes.contactItemIndicator} />

        <div className={classes.contactItemContainer}>
          <ContactDisplay
            contact={contact}
            variant="big"
            fieldTextWrap
            showSocialCreditId={isShipper}
          />
          <div data-testid="btn-contact-select" className={classes.contactItemSelectButton}>
            {!selected && <span>{strings.choose}</span>}
            {selected && (
              <>
                <CheckIcon /> <span>{strings.chosen} </span>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }),
);

interface ContactListProps {}

const ContactList: React.FC<ContactListProps> = () => {
  const {
    contacts,
    selectedIds = [],
    disabledIds = [],
    isZipcodeRequired = false,
    isEnglishNameRequired = false,
    onChangeSelectedIds,
    fetchNextPage,
    editUrl,
  } = useContext(ContactFilterContext);

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const [, setDummy] = useState({});
  const lastRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  const intersection = useIntersection(lastRef, {
    root: rootRef.current,
  });

  useEffect(() => {
    // Ensure we get latest refs after each reloading
    setDummy({});
  }, [contacts]);

  useEffect(() => {
    if (intersection?.isIntersecting && rootRef.current) {
      fetchNextPage?.();
    }
  }, [intersection?.isIntersecting, rootRef.current]);

  const createOnSelect = useEventCreator((id: number) => () => {
    if (id && selectedIds.includes(id)) {
      onChangeSelectedIds(selectedIds.filter((i) => i !== id));
    } else {
      onChangeSelectedIds([...selectedIds, id]);
    }
  });

  return (
    <div data-testid="ContactList" className={classes.contactListContainer} ref={rootRef}>
      {contacts.map((contact, i) => {
        const showMissingInfo =
          (!contact.english_name && isEnglishNameRequired) ||
          (!contact.zip_code && isZipcodeRequired);
        return (
          <div style={{ position: 'relative' }} key={contact.id}>
            {showMissingInfo && (
              <div className={classes.contactItemSelectCover}>
                <div className={classes.contactItemSelectCoverInner}>
                  {strings.missingInfo(
                    !contact.zip_code && isZipcodeRequired,
                    !contact.english_name && isEnglishNameRequired,
                    editUrl?.(contact) || '',
                  )}
                </div>
              </div>
            )}
            <ContactItem
              selected={contact.id ? selectedIds.includes(contact.id) : false}
              disabled={contact.id ? (disabledIds ?? []).includes(contact.id) : false}
              onSelect={createOnSelect(contact.id)}
              key={contact.id}
              contact={contact}
              {...(i === contacts.length - 1 && {
                ref: lastRef,
              })}
            />
          </div>
        );
      })}
    </div>
  );
};

interface ContactFilterProps {
  selectedIds: number[];
  disabledIds?: number[];
  onChangeSelectedIds: (ids: number[]) => void;
  contacts: Contact[];
  title: string;
  fetchNextPage?: () => void;
  onAddContact?: () => void;
  onEditContact?: () => void;
  onRefresh?: () => void;
  query?: ContactSearchQuery;
  onChangeQuery?: (query: ContactSearchQuery) => void;
  actions?: 'add-list' | 'modify' | 'none';
  editUrl?: (contact: Contact) => string;
  isEnglishNameRequired?: boolean;
  isZipcodeRequired?: boolean;
}

export const ContactFilter: React.FC<ContactFilterProps> = (props) => {
  const {
    contacts,
    title,
    onRefresh,
    onAddContact,
    onEditContact,
    query,
    onChangeQuery,
    actions = 'add-list',
  } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const form = useForm(ContactSearchQuery, {
    initialOutput: query,
    enableReinitialize: true,
  });

  const { fields } = form.root;

  const renderContactFilter = () => (
    <form {...form.bindForm({ onSubmit: onChangeQuery })}>
      <GridForm
        columns={3}
        fields={[fields.company_name, fields.principal_name]}
        buttons={
          <>
            <Button
              color="primary"
              size="small"
              type="submit"
              text={formStrings.search}
              style={{ width: 88 }}
            />
            <Button
              color="primary"
              size="small"
              type="submit"
              text={formStrings.reset}
              onClick={() => form.root.setOutput(empty)}
              style={{ width: 88 }}
            />
          </>
        }
      />
    </form>
  );

  const renderActions = () => (
    <div className={classes.actionsContainer}>
      {actions === 'add-list' && (
        <>
          <IconButton
            icon={<img src={addSquareIcon} alt="Add Icon" />}
            text={strings.addContact(title)}
            onClick={() => onAddContact?.()}
          />
          <IconButton
            icon={<img src={subtractSquareIcon} alt="Minus Icon" />}
            text={strings.listContact(title)}
            onClick={() => onEditContact?.()}
          />
          <IconButton
            icon={<img src={refreshIcon} alt="Refresh Icon" />}
            text={formStrings.update}
            onClick={() => onRefresh?.()}
          />
        </>
      )}
      {actions === 'modify' && (
        <>
          <IconButton
            icon={<img src={addSquareIcon} alt="Add Icon" />}
            text={strings.addOrModifyContact(title)}
            onClick={() => onAddContact?.()}
          />
        </>
      )}
    </div>
  );

  const renderNoContent = () => {
    return (
      <div className={classes.noContent}>
        {strings.noContent(
          title,
          ['modify', 'add-list'].includes(actions) ? onAddContact : undefined,
        )}
      </div>
    );
  };

  const renderNoResult = () => {
    return <div className={classes.noContent}>{strings.noResult(title)}</div>;
  };

  const isQueryEmpty = Object.values(query ?? {}).filter(Boolean).length === 0;

  return (
    <ContactFilterContext.Provider value={props}>
      <div className={classes.contactFilterContainer}>
        {renderContactFilter()}
        {renderActions()}

        {contacts.length > 0 && <ContactList />}
        {contacts.length === 0 && isQueryEmpty && renderNoContent()}
        {contacts.length === 0 && !isQueryEmpty && renderNoResult()}
      </div>
    </ContactFilterContext.Provider>
  );
};
